import residents from '../data/residents.json';
import type { Text } from '../i18n';

/** Copy for the three feature pages the landing cards open. Chinese is 書面語; English is written for Western players. */
const t = (zh: string, en: string): Text => ({ 'zh-HK': zh, en });

export type FeatureKey = 'choices' | 'neighbours' | 'manga';
export const FEATURE_KEYS: FeatureKey[] = ['choices', 'neighbours', 'manga'];
/** The landing card each page belongs to (its title, icon and tone come from there). */
export const FEATURE_CARD: Record<FeatureKey, 'f1' | 'f2' | 'f3'> = { choices: 'f1', neighbours: 'f2', manga: 'f3' };

export const LEAD: Record<FeatureKey, Text> = {
  choices: t(
    '在墨染町，故事不是寫好了等你讀。你說的每一句話、去哪裏、和誰見面，都會寫進只屬於你的章節。',
    'Nothing in Inkwell is written in advance. What you say, where you go and who you spend your evenings with all end up in chapters that exist only for you.',
  ),
  neighbours: t(
    '墨染町住着十六位鄰居。十二位可以發展戀愛，四位是照顧你的街坊與師傅。他們的生活彼此交織：你和誰走近，其他人都會看在眼裏。',
    'Sixteen people call Inkwell home. Twelve of them could become something more; four are the locals and bosses who keep an eye on you. Their lives overlap, so whoever you get close to, the rest of town notices.',
  ),
  manga: t(
    '喜歡的章節，可以畫成真正的黑白週刊漫畫：逐頁分鏡、對白嵌字、網點與光影，就像在書店買到的一樣。',
    'Turn a favourite chapter into real black-and-white manga — paneled page by page, fully lettered, with screentone and shading, like something off a bookshop shelf.',
  ),
};

/** How a chapter comes to be: four steps. */
export const STEPS: { title: Text; body: Text }[] = [
  {
    title: t('搬進墨染町', 'Move in'),
    body: t(
      '你剛搬進墨染苑 1201 室，認識住在附近的鄰居。開場章節人人相同，之後的路由你決定。',
      'You’ve just moved into Flat 1201 at Inkwell Court. Everyone plays the same opening; after that, the story is yours.',
    ),
  },
  {
    title: t('每個選擇都算數', 'Every choice counts'),
    body: t(
      '對話時，你可以靠近、說笑、坦白，或者保持距離。鄰居會記得你說過的話，並在之後的章節回應你。',
      'Lean in, tease, tell the truth or keep it to yourself. Your neighbours remember what you said, and it comes back in later chapters.',
    ),
  },
  {
    title: t('只屬於你的連載', 'A story written for you'),
    body: t(
      '開場之後，每一章都按你的選擇、你和鄰居的關係，甚至你所在城市的天氣而寫。兩位玩家選同一位鄰居，也會走出不同的故事。',
      'After the opening, every chapter is written around your choices, your relationships — even the weather where you live. Two players chasing the same neighbour won’t get the same story.',
    ),
  },
  {
    title: t('完成章節，畫成漫畫', 'Chapters become manga'),
    body: t(
      '每完成一章，故事都會收進你的漫畫書架；最喜歡的章節，更可畫成黑白週刊精繪版。',
      'Every chapter you finish lands on your manga shelf, and your favourites can be fully illustrated.',
    ),
  },
];

/** A real moment from a test chapter, shown as the choice the player meets. */
export const SAMPLE_CHOICE = {
  who: t('沈一諾', 'Chloe'),
  line: t('這句聽起來，像是你早就認定我不會失手。', 'You say that like you’ve already decided I never mess up.'),
  options: [t('笑說：那我下次先接住你的話。', 'Laugh: “Then next time, I’ll catch you first.”'), t('坦白說，那句話確實說得太滿。', 'Admit you laid it on a bit thick.')],
};

export interface Resident {
  id: string;
  name: Text;
  age: number;
  romance: boolean;
  role: Text;
  tagline: Text;
  bio: Text;
  birthday: { month: number; day: number } | null;
  /** The 人物檔案 entries open from the start; `locked` more are found in play. */
  entries: { label: Text; text: Text }[];
  locked: number;
}

/** The sixteen residents, generated from the game content by scripts/build-residents.mjs. */
export const RESIDENTS = residents as Resident[];

/** What a weekly-manga edition is. */
export const MANGA_POINTS: { title: Text; body: Text }[] = [
  {
    title: t('按劇本逐頁繪畫', 'Drawn page by page'),
    body: t(
      '每章約四至六頁，先寫分鏡劇本，再一頁頁繪畫。人物的樣子、坐在哪裏、做什麼動作，都跟着你的故事。',
      'Each chapter runs four to six pages. It’s storyboarded panel by panel, then drawn a page at a time, so who’s in the scene, where they sit and what they do all follow your story.',
    ),
  },
  {
    title: t('對白嵌字', 'Fully lettered'),
    body: t('對白與旁白在畫好之後才排版嵌入對話框，字字清晰，不會畫錯。', 'Dialogue and captions are typeset into the speech balloons, so every line is crisp and readable.'),
  },
  {
    title: t('全螢幕細看', 'Made for your phone'),
    body: t('在 App 內全螢幕閱讀，雙指放大、雙擊縮放，看清每一格的表情。', 'Read full screen in the app — pinch or double-tap to zoom in on every expression.'),
  },
  {
    title: t('按角色收藏成冊', 'Collected by character'),
    body: t('每位鄰居各有一冊，封面跟着你們的故事更新，一冊冊收藏你們的回憶。', 'Each neighbour gets their own volume, with a cover that changes as your story does.'),
  },
];

export const MANGA_PRICE = t('第一章精繪版免費，之後每章 4 畫稿點數。', 'Your first illustrated chapter is free. After that, each one is 4 manga credits.');

export const COPY = {
  more: t('了解更多', 'Learn more'),
  back: t('返回首頁', 'Back to home'),
  next: t('下一個：', 'Next: '),
  romance: t('可以戀愛的鄰居', 'Neighbours you can fall for'),
  locals: t('街坊與師傅', 'The locals'),
  age: t('{n} 歲', '{n}'),
  samples: t('精繪版試閱', 'Sample pages'),
  samplesNote: t('以下為測試章節的成品頁面：每兩頁是同一個場景，玩家選了不同的選項，就畫成不同的一頁。', 'Finished pages from test chapters. Each pair is the same scene: a different choice, so a different page.'),
  choiceTitle: t('一個真實的選擇', 'A real moment from the game'),
  close: t('關閉', 'Close'),
  profile: t('看人物檔案', 'See profile'),
  backToNeighbours: t('返回十六位鄰居', 'All neighbours'),
  birthday: t('生日', 'Birthday'),
  dossier: t('人物檔案', 'Profile'),
  lockedNote: t('還有 {n} 項檔案，要在遊戲裏和{name}熟絡之後才會知道。', '{n} more entries unlock as you get to know {name} in the game.'),
  romanceTag: t('可以戀愛', 'Romanceable'),
  localTag: t('街坊', 'Local'),
  prev: t('上一位', 'Previous'),
  nextResident: t('下一位', 'Next'),
  becomes: t('完成之後，這一章畫成了漫畫。', '…and the finished chapter, drawn as manga.'),
  start: t('開始你的故事', 'Start your story'),
};
